import { useEffect, useMemo, useState } from "react";
import { useParams } from "@/lib/router-compat";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { weeklyBriefService, type WeeklyBrief, type WeeklyBriefQuiz } from "@/services/weeklyBriefService";
import { BriefHeader } from "@/components/weekly/BriefHeader";
import { BriefProgressBar } from "@/components/weekly/BriefProgressBar";
import { EditorsNote } from "@/components/weekly/EditorsNote";
import { MarketPulse } from "@/components/weekly/MarketPulse";
import { TopStories } from "@/components/weekly/TopStories";
import { WinnersLosers } from "@/components/weekly/WinnersLosers";
import { NarrativeSection } from "@/components/weekly/NarrativeSection";
import { WeeklyListen } from "@/components/weekly/WeeklyListen";
import { BriefQuiz } from "@/components/weekly/BriefQuiz";
import { IssueArchive } from "@/components/weekly/IssueArchive";
import { PageShell } from "@/components/layout/PageShell";
import { PageHeader } from "@/components/ui/page-header";
import { useContentTranslation } from "@/hooks/useContentTranslation";
import { useT } from "@/i18n/useLocale";

const WeeklyBriefPage = () => {
  const t = useT();
  const { id } = useParams();
  const [brief, setBrief] = useState<WeeklyBrief | null>(null);
  const [quizzes, setQuizzes] = useState<WeeklyBriefQuiz[]>([]);
  const [articles, setArticles] = useState<any[]>([]);
  const [archive, setArchive] = useState<WeeklyBrief[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      const [b, all] = await Promise.all([
        id ? weeklyBriefService.getById(id) : weeklyBriefService.getLatest(),
        weeklyBriefService.listPublished(),
      ]);
      if (!mounted) return;
      setBrief(b);
      setArchive(all);

      if (b) {
        const ids = Array.from(new Set([...(b.top_story_ids ?? []), ...(b.narrative_article_ids ?? [])]));
        const [{ data: arts }, qz] = await Promise.all([
          ids.length
            ? supabase
                .from("articles")
                .select("id, title, excerpt, source_id, source_name, published_at, image_url")
                .in("id", ids)
            : Promise.resolve({ data: [] as any[] }),
          weeklyBriefService.getQuizzes(b.id),
        ]);
        if (!mounted) return;
        setArticles(arts ?? []);
        setQuizzes(qz);
      }
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, [id]);

  const topStoryArticles = useMemo(() => {
    if (!brief) return [];
    const map = new Map(articles.map((a) => [a.id, a]));
    return brief.top_story_ids.map((sid) => map.get(sid)).filter(Boolean);
  }, [brief, articles]);

  const narrativeArticles = useMemo(() => {
    if (!brief) return [];
    const map = new Map(articles.map((a) => [a.id, a]));
    return (brief.narrative_article_ids ?? []).map((sid) => map.get(sid)).filter(Boolean);
  }, [brief, articles]);

  const readMinutes = useMemo(() => {
    if (!brief) return 5;
    const words = `${brief.editors_note ?? ""} ${brief.narrative_body ?? ""}`.split(/\s+/).filter(Boolean).length;
    return Math.max(3, Math.round(words / 200) + (brief.top_story_ids?.length ?? 0));
  }, [brief]);

  const takes = brief?.top_story_takes ?? [];
  const translationFields = useMemo(() => {
    const fields: Record<string, string> = {
      headline: brief?.headline ?? "",
      teaser: brief?.teaser ?? "",
      editors_note: brief?.editors_note ?? "",
      narrative_title: brief?.narrative_title ?? "",
      narrative_body: brief?.narrative_body ?? "",
    };
    takes.forEach((take, index) => {
      fields[`take_${index}`] = take.take ?? "";
    });
    return fields;
  }, [brief, takes]);
  const tr = useContentTranslation("weekly_brief", brief?.id ?? null, translationFields);
  const translatedTakes = takes.map((take, index) => ({
    ...take,
    take: tr.text(`take_${index}`) || take.take,
  }));


  if (loading) {
    return (
      <PageShell>
        <div className="space-y-6">
          <Skeleton className="h-72 w-full rounded-3xl" />
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-28 rounded-2xl" />
            ))}
          </div>
          <Skeleton className="h-64 w-full rounded-2xl" />
        </div>
      </PageShell>
    );
  }

  if (!brief) {
    return (
      <PageShell>
        <PageHeader
          eyebrow={t("auto.weekly-brief-fc26")}
          title={t("auto.no-brief-published-yet-d245")}
          description={t("auto.the-first-weekly-brief-will-appear-here-6718")}
        />
      </PageShell>
    );
  }

  const leadImage = (topStoryArticles[0] as any)?.image_url ?? null;

  return (
    <PageShell>
      <BriefProgressBar />
      <article className="animate-fade-in">
        <BriefHeader
          issueNumber={brief.issue_number}
          weekStart={brief.week_start}
          weekEnd={brief.week_end}
          headline={tr.text("headline")}
          teaser={tr.text("teaser")}
          leadImage={leadImage}
          storyCount={brief.top_story_ids?.length ?? 0}
          readMinutes={readMinutes}
        />
        <EditorsNote body={tr.text("editors_note")} />
        <MarketPulse snapshot={brief.market_snapshot} asOf={brief.published_at ?? brief.created_at ?? brief.week_end} />
        <TopStories articles={topStoryArticles as any} takes={translatedTakes} />
        <WinnersLosers winners={brief.winners ?? []} losers={brief.losers ?? []} />
        <NarrativeSection
          title={tr.text("narrative_title")}
          body={tr.text("narrative_body")}
          articles={narrativeArticles as any}
        />
        <WeeklyListen weekStart={brief.week_start} weekEnd={brief.week_end} />
        <BriefQuiz briefId={brief.id} quizzes={quizzes} />
        <IssueArchive issues={archive} currentId={brief.id} />
      </article>
    </PageShell>
  );
};

export default WeeklyBriefPage;
